/** @jsxImportSource @emotion/react */
import { BasicHeader } from 'components';
import { useMerchantApplicationContext } from 'contexts';
import { ActivationStepKey } from 'modules/activation/ActivationConfig';
import { ActivationDashboardModule } from 'modules/activation/dashboard/ActivationDashboard.module';
import {
  ActivationStepRouteKey,
  buildActivationDashboardSteps,
  getActivationDashboardTotalSteps,
  getCompletedActivationDashboardCount,
  shouldHideSettlementDetailsForReferrer,
} from 'modules/activation/dashboard/dashboardConfigs';
import { DirectorDetailsStepModule } from 'modules/activation/steps/director-details/DirectorDetailsStep.module';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MerchantApplicationProgressResponse } from 'types/responses/activation';
import { logEvent, useActivationFunctions } from 'utils';
import BusinessDetailsPage from './BusinessDetails';
import { SettlementDetailsStepModule } from 'modules/activation/steps/settlement-details/SettlementDetailsStep.module';
import { PageAction } from 'enums';

interface DashboardPageProps {
  openHelpCentre: () => void;
}

export function DashboardPage({
  openHelpCentre,
}: DashboardPageProps): React.JSX.Element {
  const {
    applicationStatus,
    applicantFirstName,
    applicationReferrer,
    salesforceSdocContractUrl,
    merchantApplicationId,
    setFormOptions,
    setFormOptionsLoading,
  } = useMerchantApplicationContext();
  const {
    abortRequests,
    getApplicationProgressAsync,
    getApplicationFormOptionsAsync,
  } = useActivationFunctions();
  const [progress, setProgress] =
    useState<MerchantApplicationProgressResponse>();
  const [activeStepRouteKey, setActiveStepRouteKey] =
    useState<ActivationStepRouteKey>();
  const [businessDetailsInitialStep, setBusinessDetailsInitialStep] =
    useState<ActivationStepKey>(ActivationStepKey.LocationDetails);
  const directorCloseHandlerRef = useRef<(() => Promise<void>) | null>(null);

  const loadApplicationProgress = useCallback(
    async (shouldUpdate: () => boolean = () => true): Promise<void> => {
      try {
        const response = await getApplicationProgressAsync();

        if (!shouldUpdate()) {
          return;
        }

        setProgress({
          businessDetails: response.businessDetails,
          directorDetails: response.directorDetails,
          settlementDetails: response.settlementDetails,
        });
      } catch {
        if (shouldUpdate()) {
          setProgress(undefined);
        }
      }
    },
    []
  );

  useEffect(() => {
    let isMounted = true;

    loadApplicationProgress(() => isMounted);

    return (): void => {
      isMounted = false;
      abortRequests();
    };
  }, [loadApplicationProgress, abortRequests]);

  useEffect(() => {
    const fetchFormOptions = async (): Promise<void> => {
      try {
        const options = await getApplicationFormOptionsAsync();
        setFormOptions(options);
      } finally {
        setFormOptionsLoading(false);
      }
    };

    void fetchFormOptions();
  }, []);

  const handleVerify = useCallback((routeKey: ActivationStepRouteKey): void => {
    let userActionByRouteKey: PageAction;

    switch (routeKey) {
      case ActivationStepRouteKey.BusinessDetails:
        userActionByRouteKey =
          PageAction.merchantOnboardingUAEnterBusinessDetails;
        break;
      case ActivationStepRouteKey.DirectorDetails:
        userActionByRouteKey =
          PageAction.merchantOnboardingUAEnterDirectorDetails;
        break;
      case ActivationStepRouteKey.SettlementDetails:
        userActionByRouteKey =
          PageAction.merchantOnboardingUAEnterSettlementDetails;
        break;
      default:
        break;
    }

    if (userActionByRouteKey) {
      logEvent(userActionByRouteKey, { merchantApplicationId });
    }

    setActiveStepRouteKey(routeKey);
  }, []);

  const handleStepComplete = useCallback(async (): Promise<void> => {
    setActiveStepRouteKey(undefined);
    await loadApplicationProgress();
  }, [loadApplicationProgress]);

  const handleDirectorComplete = useCallback(async (): Promise<void> => {
    await loadApplicationProgress();
    if (applicationReferrer?.toLowerCase() === 'ndp') {
      setActiveStepRouteKey(undefined);
    } else {
      setActiveStepRouteKey(ActivationStepRouteKey.SettlementDetails);
    }
  }, [loadApplicationProgress, applicationReferrer]);

  const handleBusinessDetailsComplete = useCallback(async (): Promise<void> => {
    setBusinessDetailsInitialStep(ActivationStepKey.LocationDetails);
    await loadApplicationProgress();
    setActiveStepRouteKey(ActivationStepRouteKey.DirectorDetails);
  }, [loadApplicationProgress]);

  const handleDirectorBackToBusinessDetails = useCallback((): void => {
    setBusinessDetailsInitialStep(ActivationStepKey.ProvidedServices);
    setActiveStepRouteKey(ActivationStepRouteKey.BusinessDetails);
  }, []);

  const handleSettlementToDirectorDetails = useCallback((): void => {
    setActiveStepRouteKey(ActivationStepRouteKey.DirectorDetails);
  }, []);

  const handleDirectorCloseHandlerRegistration = useCallback(
    (handler: (() => Promise<void>) | null): void => {
      directorCloseHandlerRef.current = handler;
    },
    []
  );

  const shouldHideSettlementDetails =
    shouldHideSettlementDetailsForReferrer(applicationReferrer);

  const dashboardSteps = useMemo(
    () =>
      buildActivationDashboardSteps(
        progress,
        handleVerify,
        shouldHideSettlementDetails
      ),
    [handleVerify, progress, shouldHideSettlementDetails]
  );

  const completedCount = useMemo(
    () =>
      getCompletedActivationDashboardCount(
        progress,
        shouldHideSettlementDetails
      ),
    [progress, shouldHideSettlementDetails]
  );

  const totalCount = useMemo(
    () => getActivationDashboardTotalSteps(shouldHideSettlementDetails),
    [shouldHideSettlementDetails]
  );

  const headerProps = useMemo(() => {
    const baseProps = {
      alignLogoDesktop: 'space-between' as const,
      isAuthenticated: true,
      openHelpCentre,
      bottomBorder: true,
      shouldShowActivation: true,
    };

    if (!activeStepRouteKey) {
      return {
        ...baseProps,
        helpButton: 'Expanded' as const,
      };
    }

    const stepSpecificProps = {
      helpButton: 'None' as const,
      showCloseButton: true,
      showExitConfirmationDialog: true,
    };

    if (activeStepRouteKey === ActivationStepRouteKey.DirectorDetails) {
      return {
        ...baseProps,
        ...stepSpecificProps,
        customOnExitDialogText:
          "If you go back to the dashboard, your director details won't be saved.",
      };
    }

    if (activeStepRouteKey === ActivationStepRouteKey.SettlementDetails) {
      return {
        ...baseProps,
        ...stepSpecificProps,
        customOnExitDialogText:
          "If you go back to the dashboard, your settlement details won't be saved.",
      };
    }

    return { ...baseProps, ...stepSpecificProps };
  }, [activeStepRouteKey, openHelpCentre]);

  const header = <BasicHeader {...headerProps} />;

  if (activeStepRouteKey === ActivationStepRouteKey.BusinessDetails) {
    return (
      <>
        {header}
        <BusinessDetailsPage
          onComplete={handleBusinessDetailsComplete}
          initialStepKey={businessDetailsInitialStep}
        />
      </>
    );
  }

  if (activeStepRouteKey === ActivationStepRouteKey.DirectorDetails) {
    return (
      <>
        {header}
        <DirectorDetailsStepModule
          stepProgress={progress?.directorDetails}
          onReturnToDashboard={handleDirectorComplete}
          onBackToBusinessDetails={handleDirectorBackToBusinessDetails}
          onRegisterCloseHandler={handleDirectorCloseHandlerRegistration}
        />
      </>
    );
  }

  if (activeStepRouteKey === ActivationStepRouteKey.SettlementDetails) {
    return (
      <>
        {header}
        <SettlementDetailsStepModule
          stepProgress={progress?.settlementDetails}
          onBackToDirectorDetails={handleSettlementToDirectorDetails}
        />
      </>
    );
  }

  return (
    <>
      {header}
      <ActivationDashboardModule
        firstName={applicantFirstName}
        completedCount={completedCount}
        totalCount={totalCount}
        steps={dashboardSteps}
        applicationStatus={applicationStatus}
        salesforceSdocContractUrl={salesforceSdocContractUrl}
      />
    </>
  );
}
